/**
 * useXrefDialog — Track 3 Phase 4
 *
 * Owns the XREF Manager dialog feature (Phase FIN.3) extracted from
 * `App.tsx` as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns the `xrefManagerDialogOpen` local state and exposes
 *     imperative `open()` / `close()` for the command-line router and
 *     `resetShellState()`.
 *   - Owns the per-action handlers (`onAttachFile`, `onUnload`,
 *     `onReload`, `onBind`, `onDetach`, `onClip`, `onOpen`) — each
 *     dispatches the matching `file/x*` command and emits a localized
 *     command-feed entry.
 *   - The XATTACH/XBIND/XOPEN/XEDIT/XCLIP commands all share this single
 *     dialog (AutoCAD parity — the manager is the central surface).
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: { xrefRegistry, onAttachFile, onUnload, onReload, onBind,
 *              onDetach, onClip, onOpen, onClose },
 *     open,
 *     close,
 *   }
 *
 * The orchestrator mounts `<XrefManagerDialog>` when `isOpen` and
 * spreads `props`. `open()` is wired into the command-line router for
 * the XREF / XATTACH / XBIND / XCLIP / XOPEN / XUNLOAD / XRELOAD /
 * XDETACH aliases.
 *
 * AutoCAD parity:
 *   The XrefManagerDialog component preserves the full AutoCAD-parity
 *   surface (table of xrefs, per-row Unload/Reload/Bind/Detach/Clip
 *   buttons, Attach DWG/DXF... button). The hook only orchestrates the
 *   open/close flag, file-picker round-trips, and dispatch wiring.
 */

import { useCallback } from "react";

import { useDialogOpenState } from "./useDialogOpenState";

import { importDxfStringToDrawingDocument } from "@dxf-ai-editor/drawing-model";
import { decodeDxfBytes } from "@dxf-ai-editor/dxf-io";

import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";

// Phase 1.k (П35Р React #185 fix) — stable empty xref registry sentinel.
// Returning a fresh `{}` inside the Zustand selector defeated
// `useSyncExternalStore` snapshot bail-out, looping forceStoreRerender
// → commitHookEffectListMount on every store update.
const EMPTY_XREF_REGISTRY: NonNullable<
  ReturnType<typeof useEditorStore.getState>["geometryDocument"]["xrefRegistry"]
> = {};

export interface UseXrefDialogParams {
  /** Receives localized success / error feed entries. */
  appendCommandFeed: (text: string) => void;
}

export interface UseXrefDialogResult {
  /** Whether to mount `<XrefManagerDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<XrefManagerDialog>`. */
  props: {
    xrefRegistry: NonNullable<
      ReturnType<typeof useEditorStore.getState>["geometryDocument"]["xrefRegistry"]
    >;
    onAttachFile: (file: File, attachmentType: "attach" | "overlay") => Promise<void>;
    onUnload: (payload: { xrefName: string }) => void;
    onReload: (payload: { xrefName: string }, file?: File) => Promise<void>;
    onBind: (payload: { xrefName: string }) => void;
    onDetach: (payload: { xrefName: string }) => void;
    onClip: (payload: { xrefName: string }) => void;
    onOpen: (payload: { xrefName: string }) => void;
    onClose: () => void;
  };
  /** Imperative open — used by the command-line router (XREF/XATTACH/...). */
  open: () => void;
  /** Imperative close — used by `resetShellState()` and `onClose`. */
  close: () => void;
}

export function useXrefDialog({
  appendCommandFeed,
}: UseXrefDialogParams): UseXrefDialogResult {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);
  const xrefRegistry = useEditorStore(
    (store) => store.geometryDocument.xrefRegistry ?? EMPTY_XREF_REGISTRY,
  );
  const activeSpaceId = useEditorStore(
    (store) => store.state.drawing.activeSpaceId,
  );

  const { isOpen, open, close } = useDialogOpenState();

  const onAttachFile = useCallback(
    async (file: File, attachmentType: "attach" | "overlay") => {
      try {
        // T9 IMPORT fix — codepage-aware decode (`$DWGCODEPAGE` → TextDecoder)
        // instead of `file.text()` (WHATWG UTF-8 only → mojibake on a
        // non-UTF-8 xref DXF). Same byte boundary as the main import path.
        const dxfText = decodeDxfBytes(await file.arrayBuffer());
        // Parse the external DXF and harvest its model-space entities to
        // serve as the xref BLOCK's children. We use the same import
        // bridge that powers DXF Import; everything outside the model
        // space (layouts, blocks, layers) is dropped — xref content is
        // treated as a flat collection of geometry by AutoCAD too.
        const externalDoc = importDxfStringToDrawingDocument(dxfText);
        const modelSpace = externalDoc.spaces.find((s) => s.kind === "model");
        const entities = modelSpace
          ? // Strip the source spaceId; the mutation re-parents under the
            // synthesised xref space id.
            (modelSpace.entities.map((e) => ({ ...e })) as unknown[])
          : [];
        const xrefName = file.name.replace(/\.(dxf|dwg)$/i, "");
        dispatch({
          type: "file/xattach",
          source: "commandline",
          xrefName,
          xrefPath: file.name,
          xrefEntities: entities,
          attachmentType,
          insertionPoint: { x: 0, y: 0 },
          insertSpaceId: activeSpaceId,
        });
        appendCommandFeed(
          t("summary.xref_attached", { name: xrefName, path: file.name }),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendCommandFeed(t("toast.xref.attach_failed", { message }));
      }
    },
    [dispatch, activeSpaceId, appendCommandFeed, t],
  );

  const onReload = useCallback(
    async (payload: { xrefName: string }, file?: File) => {
      try {
        let entities: unknown[] | undefined;
        let newPath: string | undefined;
        if (file) {
          // T9 IMPORT fix — codepage-aware decode (see onAttachFile above).
          const dxfText = decodeDxfBytes(await file.arrayBuffer());
          const externalDoc = importDxfStringToDrawingDocument(dxfText);
          const modelSpace = externalDoc.spaces.find(
            (s) => s.kind === "model",
          );
          entities = modelSpace
            ? (modelSpace.entities.map((e) => ({ ...e })) as unknown[])
            : [];
          newPath = file.name;
        }
        dispatch({
          type: "file/xreload",
          source: "commandline",
          xrefName: payload.xrefName,
          ...(entities ? { newEntities: entities } : {}),
          ...(newPath !== undefined ? { newPath } : {}),
        });
        appendCommandFeed(
          t("summary.xref_reloaded", { name: payload.xrefName }),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendCommandFeed(t("toast.xref.reload_failed", { message }));
      }
    },
    [dispatch, appendCommandFeed, t],
  );

  const onUnload = useCallback(
    (payload: { xrefName: string }) => {
      try {
        dispatch({
          type: "file/xunload",
          source: "commandline",
          xrefName: payload.xrefName,
        });
        appendCommandFeed(
          t("summary.xref_unloaded", { name: payload.xrefName }),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendCommandFeed(t("toast.xref.unload_failed", { message }));
      }
    },
    [dispatch, appendCommandFeed, t],
  );

  const onBind = useCallback(
    (payload: { xrefName: string }) => {
      try {
        dispatch({
          type: "file/xbind",
          source: "commandline",
          xrefName: payload.xrefName,
        });
        appendCommandFeed(t("summary.xref_bound", { name: payload.xrefName }));
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendCommandFeed(t("toast.xref.bind_failed", { message }));
      }
    },
    [dispatch, appendCommandFeed, t],
  );

  const onDetach = useCallback(
    (payload: { xrefName: string }) => {
      try {
        dispatch({
          type: "file/xdetach",
          source: "commandline",
          xrefName: payload.xrefName,
        });
        appendCommandFeed(
          t("summary.xref_detached", { name: payload.xrefName }),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendCommandFeed(t("toast.xref.detach_failed", { message }));
      }
    },
    [dispatch, appendCommandFeed, t],
  );

  const onClip = useCallback(
    (payload: { xrefName: string }) => {
      // Minimal XCLIP — apply a default rectangle covering bounds from
      // (-50,-50) to (50,50). A future revision adds the rubberband
      // boundary picker; for Phase FIN.3 we provide an applied default
      // so the round-trip and registry semantics are exercised end-to-end.
      try {
        dispatch({
          type: "file/xclip",
          source: "commandline",
          xrefName: payload.xrefName,
          boundary: {
            type: "rectangle",
            vertices: [
              { x: -50, y: -50 },
              { x: 50, y: 50 },
            ],
          },
        });
        appendCommandFeed(
          t("summary.xref_clipped", { name: payload.xrefName }),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendCommandFeed(t("toast.xref.clip_failed", { message }));
      }
    },
    [dispatch, appendCommandFeed, t],
  );

  const onOpenXref = useCallback(
    (payload: { xrefName: string }) => {
      // XOPEN — AutoCAD opens the xref in a new editing window. In the
      // browser we cannot spawn a separate editor instance with a
      // file-system reference, so we surface a toast pointing the user
      // at the manager. A future enhancement could use `window.open`
      // with a deep link that downloads the cached content.
      appendCommandFeed(
        t("toast.xref.open_unsupported", { name: payload.xrefName }),
      );
    },
    [appendCommandFeed, t],
  );

  return {
    isOpen,
    props: {
      xrefRegistry,
      onAttachFile,
      onUnload,
      onReload,
      onBind,
      onDetach,
      onClip,
      onOpen: onOpenXref,
      onClose: close,
    },
    open,
    close,
  };
}
